import { useState } from 'react'
import dayjs from '@dcp/shared/utils/dayjs'
import {
  KpiCards,
  BalanceImportFilters,
  BalanceImportTabs,
  BalanceImportTable,
} from '@/components/banking'
import {
  useBankingKpi,
  useBalanceImportStatus,
  useFacilities,
} from '@/hooks/useBanking'
import { tableQueryState } from '@/components/common/queryErrorDisplay'
import { useTablePagination } from '@/hooks/useTablePagination'
import type { BankingFilters, BalanceImportStatus, BalanceImportTab } from '@/types/banking'

const EMPTY_STATUS_ROWS: BalanceImportStatus[] = []

/**
 * Balance Import page
 * Displays balance import status with filters, KPI cards, and tabbed interface
 */
export function BalanceImportPage() {
  const [filters, setFilters] = useState<BankingFilters>({
    endDate: dayjs().format('YYYY-MM-DD'),
    facilityIds: undefined,
  })
  // Default to the "Bank balance update" tab (DCP-120/DCP-267): the inline-edit
  // table, which is also the first tab.
  const [activeTab, setActiveTab] = useState<BalanceImportTab>('balance-manual-update')

  // Bank tab paging. A facility change starts over on page 1 (the date filter
  // does not scope this table). The total arrives WITH the page
  // (`{ items, total }`), so it goes through `forTotal` below.
  const pagination = useTablePagination({
    totalItems: undefined,
    resetKey: JSON.stringify(filters.facilityIds ?? []),
  })

  // Fetch data
  const { data: kpi, isLoading: kpiLoading } = useBankingKpi(filters)
  const statusQuery = useBalanceImportStatus({
    page: pagination.page,
    pageSize: pagination.pageSize,
    facilityIds: filters.facilityIds,
  })
  // Rows can leave the result (an account excluded from Cash Position, a
  // facility deactivated): `forTotal` moves to the new last page rather than
  // leaving the user on an empty one.
  const { dataTablePagination } = pagination.forTotal({
    total: statusQuery.data?.total,
    isFetching: statusQuery.isFetching,
  })
  const statusRows = statusQuery.data?.items ?? EMPTY_STATUS_ROWS
  const { data: facilities } = useFacilities()

  return (
    <div className="space-y-6">
      {/* Filters */}
      {facilities && (
        <BalanceImportFilters
          filters={filters}
          onFiltersChange={setFilters}
          facilities={facilities}
        />
      )}

      {/* Aggregated balances (DCP-297): rendered unconditionally and told about
          the loading state, like every other Banking tab — otherwise the row
          pops into existence when the query resolves and shifts the page. */}
      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/* Tabs with Balance Import Table */}
      <BalanceImportTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        facilityIds={filters.facilityIds}
      >
        <BalanceImportTable
          data={statusRows}
          pagination={dataTablePagination}
          {...tableQueryState(statusQuery, statusRows.length)}
        />
      </BalanceImportTabs>
    </div>
  )
}